// libraries
import type { CreditRatingDisplayType, OfferInfoState, StateTree } from '@makemydeal/dr-dash-types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { COMMON_CONSUMER_APPLICATION_NAME, PERSONA_APPNAME_RELATION, VERSION_CREATED_BY_TO_APP_DISPLAY_NAME } from '../constants';
import * as dealXgDetailsSelectors from './dealXgDetailsSelectors';
import * as offerReduxSelectors from './offerRedux';
import * as paymentSelectors from './payment';

const getOfferInfoState = (state: StateTree): OfferInfoState => {
    return state.offerInfo ?? {};
};

export const getOfferId = (state: StateTree) => {
    const offer = getOfferInfoState(state);
    return offer.offerId;
};

export const getDealXgVersion = (state: StateTree) => {
    const offer = getOfferInfoState(state);
    return offer.dealXgVersion;
};

export const getDealCreatedDate = (state: StateTree) => {
    const offer = getOfferInfoState(state);
    return offer.dealCreatedDate;
};

export const getHasBeenSent = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.hasBeenSent;
};

export const getIsOfferBeingSubmitted = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.isOfferBeingSubmitted;
};

export const getHasBeenSaved = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.hasBeenSaved;
};

export const getIsOfferBeingSaved = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.isOfferBeingSaved;
};

export const getHasDraftBeenSaved = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.hasDraftBeenSaved;
};

export const getIsDraftBeingSaved = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.isDraftBeingSaved;
};

export const getLastSuccessfulSaveDateString = (state: StateTree): string | undefined => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.lastSuccessfulSaveDateString;
};

export const getIsMakingCurrent = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.isMakingCurrent;
};

export const getIsVersionSpecificLoad = (state: StateTree): boolean => {
    return getOfferInfoState(state).isVersionSpecificLoad ?? false;
};

export const getGtlBannerShownOnLoad = (state: StateTree): boolean => {
    return getOfferInfoState(state).gtlBannerShownOnLoad ?? false;
};

export const getMergedDeal = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.mergedDeal;
};

export const isTestDriveReadyBeforeOffer = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.isTestDriveReadyBeforeOffer;
};

export const hasLinks = (state: StateTree) => !!getShopperURL(state);

export const getCreditAppURL = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.urls?.creditAppURL ?? '';
};

export const getShopperURL = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.urls?.shopperURL ?? '';
};

export const getTestDriveURL = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.urls?.testDriveURL ?? '';
};

export const getDocUploadURL = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.urls?.docUploadURL ?? '';
};

export const getUrlsLoading = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.urlsLoading ?? false;
};

export const getUrlsError = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.urlsError;
};

export const getSocketEventNotification = (state: StateTree) => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.socketEventNotification;
};

export const getSocketEventUpdatedMilestones = (state: StateTree) => {
    return getSocketEventNotification(state)?.updatedMilestones ?? [];
};

export const getSocketEventIsPaymentUpdate = (state: StateTree) => {
    const paymentActivities = ['tradeIn', 'vehicleProtectionProducts', 'incentives', 'accessories'];
    const milestoneList = getSocketEventUpdatedMilestones(state);
    return milestoneList.filter((item) => paymentActivities.includes(item)).length > 0;
};

export const getSocketEventAppName = (state: StateTree): string => {
    const { changedBy, dealStructure } = getSocketEventNotification(state) || {};
    const createdByAppName = dealStructure?.versionCreatedBy?.applicationName;
    if (createdByAppName) {
        const appName = VERSION_CREATED_BY_TO_APP_DISPLAY_NAME[createdByAppName];
        if (appName) {
            return appName;
        }
    }
    let persona = changedBy?.persona ?? '';
    if (dealerSelectors.isCustomerViewExperience(state) && changedBy?.applicationName === 'Customer View') persona = 'customer';
    return PERSONA_APPNAME_RELATION[persona.toLocaleLowerCase()] ?? '';
};

export const getSocketEventDealVersion = (state: StateTree): string => {
    return getSocketEventNotification(state)?.message.version ?? '';
};

export const getSocketEventComment = (state: StateTree): string => {
    return getSocketEventNotification(state)?.dealStructure?.versionComment ?? '';
};

export const getSocketEventAcceptedByBuyer = (state: StateTree): boolean => {
    return getSocketEventNotification(state)?.dealStructure?.acceptedByBuyer ?? false;
};

export const getIsSocketEventCommonConsumerSourced = (state: StateTree): boolean =>
    getSocketEventNotification(state)?.dealStructure?.versionCreatedBy?.applicationName?.toLowerCase() ===
    COMMON_CONSUMER_APPLICATION_NAME;

export const getShowInvalidCoBuyerModal = (state: StateTree): boolean => getOfferInfoState(state).showInvalidCoBuyerModal ?? false;

export const getEcommercePendingCancellationStatus = (state: StateTree): boolean => {
    const isEcommerceDeal = dealXgDetailsSelectors.getIsEcommerceDeal(state);
    const penciledInputs = offerReduxSelectors.getPenciledInputs(state);
    const savedOfferType = offerReduxSelectors.getSavedOfferType(penciledInputs);
    const currentOfferType = offerReduxSelectors.getCurrentOfferType(state);
    const savedSpecialPaymentFormula = offerReduxSelectors.getSavedSpecialPaymentFormula(penciledInputs);
    const currentSpecialPaymentFormula = offerReduxSelectors.getSpecialPaymentFormula(state);

    const offerTypeSwitched = savedOfferType != currentOfferType;
    const specialPaymentFormulaSwitched = savedSpecialPaymentFormula != currentSpecialPaymentFormula;

    return isEcommerceDeal && (offerTypeSwitched || specialPaymentFormulaSwitched);
};

export const getSoftPullConsentProvided = (state: StateTree): boolean | undefined => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.softPullConsentProvided;
};

export const getCreditRatingDisplayType = (state: StateTree): CreditRatingDisplayType | undefined => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.creditRatingDisplayType;
};

export const getShowSoftPullConsentModal = (state: StateTree): boolean | undefined => {
    const offerInfoState = getOfferInfoState(state);
    return offerInfoState.showSoftPullConsentModal;
};

export const getSearchAndMergeCleanedUp = (state: StateTree): boolean => getOfferInfoState(state).searchAndMergeCleanedUp ?? false;

export const getPendingReview = (state: StateTree) => {
    return getOfferInfoState(state).pendingReview;
};

export const getPendingReviewHandledVersion = (state: StateTree) => {
    return getOfferInfoState(state).pendingReviewHandledVersion;
};

export const getPendingReviewResolution = (state: StateTree) => {
    return getOfferInfoState(state).pendingReviewResolution;
};

export const getHasSuccessfulPaymentSinceInit = (state: StateTree): boolean =>
    getOfferInfoState(state).hasSuccessfulPaymentSinceInit === true;

export const getMathStackWarningVisible = (state: StateTree): boolean =>
    getHasSuccessfulPaymentSinceInit(state) && !paymentSelectors.isCalculatingPayment(state) && !paymentSelectors.getIsError(state);
